import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  HttpLink,
  InMemoryCache,
  LazyQueryHookOptions,
  MutationHookOptions,
  OperationVariables,
  QueryHookOptions,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/utilities";
import type { ObservableSubscription } from "@apollo/client/utilities";
import type { FetchResult } from "@apollo/client/link/core";
import type { AccountInfo } from "@azure/msal-browser";
import { msalInstance } from "../msalInstance";
import { getAccessToken } from "./authToken";
import { loginRequest } from "../authConfig";

// this is the endpoint for graphql mock api
const MOCK_URL = "/graphql";
// this is a fictive graphql api endpoint to use for demo purposes
const REAL_URL = `${process.env.GATSBY_GAPI_BASE_URL}`;
const httpLink = new HttpLink({ uri: REAL_URL });

// Resolves the bearer token for each request through MSAL (acquireTokenSilent
// returns the cached token if still valid, renews it if expired). This is what
// makes a deleted/invalid token or wiped MSAL cache trigger re-authentication
// on the very next API call instead of waiting for the server to 401.
async function resolveToken(): Promise<string> {
  if (typeof window === "undefined") {
    return "";
  }
  const account = msalInstance.getAllAccounts()[0];
  if (!account) {
    // MSAL has no session at all (storage cleared / signed out).
    localStorage.removeItem("token");
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch {
      // interaction already in progress; the pending redirect wins.
    }
    return "";
  }
  return getAccessToken(msalInstance, account);
}

const authLink = setContext(async (_, { headers }) => {
  const token = await resolveToken();
  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
      "Api-Version": `${process.env.GATSBY_API_VERSION}`,
      "Ocp-Apim-Subscription-Key": `${process.env.GATSBY_HEADER_KEY}`,
    },
  };
});

// Apollo's authLink reads the token MSAL last cached in localStorage; if that
// token has gone stale (e.g. the SPA refresh token hit Entra's 24h hard
// expiry - AADSTS700084) the API call comes back 401 with no other signal.
// On a 401, re-check the token via MSAL rather than redirecting outright: if
// silent renewal still succeeds, the 401 wasn't caused by a stale token (e.g.
// a permission error) and getAccessToken() resolves without redirecting -
// it only redirects when MSAL itself confirms re-authentication is required.
// Concurrent 401s share one in-flight renewal (instead of each independently
// racing acquireTokenRedirect), and the operation that triggered the 401 is
// retried with the refreshed token instead of being left failed.
let reauthPromise: Promise<string> | null = null;
function reacquireToken(account: AccountInfo): Promise<string> {
  if (!reauthPromise) {
    reauthPromise = getAccessToken(msalInstance, account).finally(() => {
      reauthPromise = null;
    });
  }
  return reauthPromise;
}

const errorLink = onError(({ networkError, operation, forward }) => {
  const statusCode = (networkError as { statusCode?: number })?.statusCode;
  if (statusCode !== 401 || typeof window === "undefined") {
    return undefined;
  }
  const account = msalInstance.getAllAccounts()[0];
  if (!account) {
    return undefined;
  }

  return new Observable<FetchResult>(observer => {
    let innerSub: ObservableSubscription | undefined;
    let tornDown = false;

    reacquireToken(account)
      .then(accessToken => {
        if (tornDown) {
          return;
        }
        if (!accessToken) {
          // Re-authentication was required; the user is being redirected,
          // so there's nothing left to retry on this page.
          observer.complete();
          return;
        }
        // No need to set the Authorization header on this operation's
        // context: authLink re-reads localStorage["token"] on every forward
        // (including this retry), and getAccessToken() already wrote the
        // fresh token there as part of resolving accessToken above.
        innerSub = forward(operation).subscribe({
          next: result => observer.next(result),
          error: err => observer.error(err),
          complete: () => observer.complete(),
        });
      })
      .catch(error => {
        if (!tornDown) {
          observer.error(error);
        }
      });

    return () => {
      tornDown = true;
      innerSub?.unsubscribe();
    };
  });
});

const link = ApolloLink.from([errorLink, authLink, httpLink]);

// this is an instance of the Apollo Client that uses the 'real' GraphQL API
const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    // dataIdFromObject: o => {
    //   if(o.__typename == 'HotelActiveContactsByHotelId') {
    //     return `HotelActiveContactsByHotelId:${o.id}:${o.contactConnectionId}`;
    //   }
    //   return defaultDataIdFromObject(o);
    // },
    typePolicies: {
      HotelActiveContactsByHotelId: {
        keyFields: ["id", "contactConnectionId"],
      },
    },
  }),
  link: link,
});
// this is an instance of the Apollo Client that uses the mock GraphQL API
const apolloMockClient = new ApolloClient({
  uri: MOCK_URL,
  cache: new InMemoryCache(),
});
// this function return either the mock or real Graphql Apollo client instance to make API calls
export function getApolloClient(useMock = true) {
  return useMock ? apolloMockClient : apolloClient;
}
// this returns the options to be used in the useLazyQuery hook
export function getUseLazyQueryHookOptions(useMock = true) {
  const useThisClient = useMock ? apolloMockClient : apolloClient;
  const options: LazyQueryHookOptions = {
    notifyOnNetworkStatusChange: true,
    client: useThisClient,
  };
  return options;
}
// this returns the options to be used in the useQuery hook
export function getUseQueryHookOptions(
  useMock = true,
  variables: OperationVariables | undefined = undefined
) {
  const useThisClient = useMock ? apolloMockClient : apolloClient;
  const options: QueryHookOptions = {
    notifyOnNetworkStatusChange: true,
    client: useThisClient,
  };
  if (variables) options.variables = variables;
  return options;
}

// this returns the options to be used in the useMutation hook
export function getUseMutationHookOptions(useMock = true) {
  const useThisClient = useMock ? apolloMockClient : apolloClient;
  const options: MutationHookOptions = {
    notifyOnNetworkStatusChange: true,
    client: useThisClient,
  };
  return options;
}
